import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { Button } from "../../../components/Button";
import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { UpdateKanbanCardBetweenKanbansDto } from "../../../model/kanban/dto/UpdateKanbanCardBetweenKanbansDto";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { AtiraToast } from "../../../utils/AtiraToast";
import {
  getTranslatedKanbanColumnName,
  getTranslatedKanbanName,
} from "../KanbanUtils";
import { useDrawersContext } from "../../tasks/drawers-context";

const StyledSelect = styled(AtiraSelect)`
  .ant-select {
    border: 1px solid
      ${({ theme, valid }) => (valid ? theme.transparent : theme.danger)} !important;
  }

  border-radius: ${Rounded.md};
`;

export const KanbanCardMoveToOtherKanbanDrawer: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const {
    kanbanCard: card,
    kanbanCardMoveToOtherBoardVisible,
    setKanbanCardMoveToOtherBoardVisible,
  } = useDrawersContext();
  const dispatch = useAppDispatch();

  const kanbansMeta = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);
  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const { handleSubmit, control, getValues, watch, reset } =
    useForm<UpdateKanbanCardBetweenKanbansDto>();

  const [selectedDestinationKanbanId] = watch(["destinationKanbanId"]);

  const kanbanOptions =
    kanbansMeta?.map((kanban) => ({
      label: getTranslatedKanbanName(kanban.name),
      value: kanban._id,
    })) || [];

  const columnOptions =
    kanbansMeta
      ?.find(({ _id }) => _id === selectedDestinationKanbanId)
      ?.columns?.filter((c) => !c.default)
      ?.map((c) => ({
        label: getTranslatedKanbanColumnName(c.name, kanban?.template),
        value: c._id,
      })) || [];

  const localeOnClose = () => {
    reset();
    setKanbanCardMoveToOtherBoardVisible(false);
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      await dispatch(
        kanbanActions.updateKanbanCardBetweenKanbans(getValues()),
      ).unwrap();

      await Promise.all([
        dispatch(
          kanbanActions.getKanbanCardsByKanbanColumnId({
            columnId: getValues("sourceColumnId")!,
            userId,
          }),
        ),
        dispatch(
          kanbanActions.getKanbanCardsByKanbanColumnId({
            columnId: getValues("destinationColumnId")!,
            userId,
          }),
        ),
        dispatch(kanbanActions.getUserKanbansMeta({ userId })),
      ]);

      await dispatch(
        kanbanActions.getUserKanban({
          userId,
          kanbanId: getValues("sourceKanbanId"),
        }),
      );

      AtiraToast.success(t("kanban.card.moveBetweenColumns.success"));
      localeOnClose();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reset({
      sourceKanbanId: card?.column?.parentId || "",
      oldOrder: card?.order || 0,
      userId,
      cardId: card?._id || "",
      sourceColumnId: card?.parentId || "",
    });
  }, [
    card?._id,
    card?.column?.parentId,
    card?.order,
    card?.parentId,
    reset,
    userId,
  ]);

  if (!card) {
    return null;
  }

  return (
    <Drawer
      open={kanbanCardMoveToOtherBoardVisible}
      onClose={localeOnClose}
      title={t("kanban.card.moveBetweenColumns.title", { name: card?.title })}
    >
      <Flex gap="m" flexDirection="column">
        {/* <Controller
          control={control}
          name="sourceKanbanId"
          render={({ field: { value }, fieldState: { error } }) => (
            <StyledSelect
              title={t("kanban.card.moveBetweenColumns.source.title")}
              value={kanban?.name}
              defaultValue={kanban?.name}
              onChange={noop}
              options={kanbanOptions}
              disabled
              valid={!error}
              errorMessage={error?.message}
            />
          )}
        /> */}

        <Controller
          control={control}
          name="destinationKanbanId"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledSelect
              title={t("kanban.card.moveBetweenColumns.target.title")}
              value={value}
              onChange={onChange}
              options={kanbanOptions.filter(
                ({ value }) => value !== kanban?._id,
              )}
              disabled={loading}
              valid={!error}
              errorMessage={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="destinationColumnId"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledSelect
              title={t("kanban.card.moveBetweenColumns.column.title")}
              value={value}
              onChange={onChange}
              options={columnOptions}
              disabled={loading || !selectedDestinationKanbanId}
              valid={!error}
              errorMessage={error?.message}
            />
          )}
        />

        <Button width="100%" onClick={handleSubmit(onSubmit)} loading={loading}>
          {t("common.save")}
        </Button>
      </Flex>
    </Drawer>
  );
};
